import * as ngCore from '@angular/core'
import type * as ents from '@attain-apps/entities'
import * as ec from '@attain-apps/fe/web/main/lib/entity-collection'
import { TypeAsserter } from '@attain-apps/type-guards'
import * as rxjs from 'rxjs'

import * as connectorDefinitions from '../connector-definitions'
import * as gql from '../gql'

import type { RunRequest } from './runs/actions'
import type { TypeParams } from './types'

/** GraphQL CRUDI and execution API for zone-scoped connectors. */
@ngCore.Injectable({ providedIn: 'root' })
export class Api implements ec.models.Api<TypeParams> {
  /** Definition collection used to resolve connector definition names. */
  private readonly _definitions = ngCore.inject(connectorDefinitions.Collection)

  /** @inheritdoc */
  public readonly list = ec.models.Api.list.using.nothingButContext<TypeParams>(
    context =>
      context ? gql.queries.connectors.execute(context) : rxjs.of([]),
  )

  /** @inheritdoc */
  public readonly get: ec.models.Api<TypeParams>['get'] = (context, id) => {
    TypeAsserter.assertIsNotNullish(context)
    return gql.queries.connector.execute({ ...context, id })
  }

  /** @inheritdoc */
  public readonly create: ec.models.Api<TypeParams>['create'] = (
    context,
    { apiKey, ...connector },
  ) => {
    TypeAsserter.assertIsNotNullish(context)
    return gql.mutations.createConnector.execute({
      ...context,
      apiKey,
      input: gql.inputs.connector.INPUT.getGqlFrom(connector),
    })
  }

  /** @inheritdoc */
  public readonly update: ec.models.Api<TypeParams>['update'] = (
    context,
    { id, updates },
  ) => {
    TypeAsserter.assertIsNotNullish(context)
    return gql.mutations.updateConnector.execute({
      ...context,
      id,
      updates: gql.inputs.connector.UPDATES.getGqlFrom(updates),
    })
  }

  /** @inheritdoc */
  public readonly delete: ec.models.Api<TypeParams>['delete'] = (
    context,
    id,
  ) => {
    TypeAsserter.assertIsNotNullish(context)
    return gql.mutations.deleteConnector.execute({ ...context, id })
  }

  /** @inheritdoc */
  public readonly getAttributes: ec.models.Api<TypeParams>['getAttributes'] =
    () =>
      rxjs.of([
        ec.models.Attribute.fromKey<TypeParams>('id', { name: 'ID' }),
        ec.models.Attribute.fromKey<TypeParams>('name', { name: 'Name' }),
        {
          getValueFrom: connector =>
            this._definitions.plugins.currentListPlugin.feature.selectors.entities
              .asSignal()
              .find(definition => definition.id === connector.definitionId)
              ?.name ?? connector.definitionId,
          id: 'definitionName',
          name: 'Definition',
        },
        ec.models.Attribute.fromKey<TypeParams>('baseUrl', {
          name: 'Base URL',
        }),
      ])

  /** Runs one persisted connector in the supplied zone. */
  public run(
    context: ents.core.admin.Context.ForZone,
    request: RunRequest,
  ): rxjs.Observable<boolean> {
    return gql.mutations.runConnector.execute({
      ...context,
      connectorId: request.connectorId,
      truncateModelIds: [...request.truncateModelIds],
    })
  }

  /** Subscribes to persisted progress for one connector's active run. */
  public subscribeRun(
    context: ents.core.admin.Context.ForZone,
    connectorId: ents.core.integrations.Connector.Id,
  ): rxjs.Observable<gql.subscriptions.onConnectorRunPublished.PublishedRun> {
    return gql.subscriptions.onConnectorRunPublished.executor({
      ...context,
      connectorId,
    })
  }
}
